import { AccountsReceivableReportSource } from "api";
import { settlementQuery } from "helpers/mutualSettlements";
import { regionTypesEnum } from "helpers/enums";
import { GEOZONE_FIELDS_BY_REGION_TYPES } from "helpers/map";
import { REPORT_TYPES } from "helpers/reports";
import { buildQuery } from "helpers/reqparams";
import { ITEM_ROOT } from "odata-query";
import { body, downloadFileHandler, request } from "services/request";
import { accountsReceivableReportDemo, incomesAndExpensesReportDemo } from "./demo";

const resource = "api/report";

type ReportParamsModel = {
    id: keyof typeof REPORT_TYPES;
    startDate: Date;
    endDate: Date;
    multipleCarTypeId: number;
    carTypeId: number;
    transportColumnId: number[];
    onlyWithoutApp: boolean;
    marginalityValues: "all" | "estimated" | "fact";
    visitStatus: string[];
    deviationVolume?: { key: string; value: number };
    legalPersonId?: number[];
    logistIds?: number[];
    trailerTypeIds?: number[];
    trailerTypeFamilyTypeIds?: number[];
    transportColumnIds?: number[];
    month?: string | Date;
    reportPeriod?: string | Date;
    groupByRegion?: boolean;
    groupByDates?: boolean;
    groupByCurrentLogist?: boolean;
    onlyOverdue?: boolean;
    groupByCounterparties?: boolean;
    previousDayPaymentsOnly?: boolean;
    byPlanPaymentDate?: boolean;
    counterpartyId?: number;
    carId?: number;
    paymentStatus?: string | string[];
    source?: AccountsReceivableReportSource;
    isAccountsPayable?: boolean;
    region?: { id: number; type: keyof typeof GEOZONE_FIELDS_BY_REGION_TYPES };
};

type ReportCounterpartyParamsModel = {
    id: "CounterpartiesReport";
    startDate: Date;
    endDate: Date;
    trailerTypeId: number;
    legalPersonId: number[] | undefined;
    firstPoint: { type: keyof typeof regionTypesEnum; id: number };
    lastPoint: { type: keyof typeof regionTypesEnum; id: number };
    groupByRegion?: boolean;
};

const index = (section: string) =>
    request(`${resource}/getlist${section ? `?section=${section}` : ""}`, { method: "GET" }).then((items) =>
        items?.map((item: { id: string; name: string }) =>
            item.id === REPORT_TYPES.AccountsReceivableReport ? { ...item, name: "Взаиморасчёты" } : item,
        ),
    );

const getMetadata = (params: { id: number }) => {
    return request(`${resource}/getMetadata/${params.id}`, { method: "GET" });
};

const reportsOdataCounterpartiesQuery = (params: ReportCounterpartyParamsModel) => {
    const { startDate, endDate, trailerTypeId, legalPersonId, ...rest } = params;
    const { firstPoint, lastPoint } = rest;
    const pointFilter = (pointKey: "firstPoint" | "lastPoint") => {
        const value = rest[pointKey];
        switch (value.type) {
            case regionTypesEnum.Country:
                return { [`${pointKey}/countryId`]: value.id };
            case regionTypesEnum.FederalDistrict:
                return { [`${pointKey}/federalDistrictId`]: value.id };
            case regionTypesEnum.FederalSubject:
                return { [`${pointKey}/stateId`]: value.id };
            case regionTypesEnum.City:
                return { [`${pointKey}/cityId`]: value.id };
            case regionTypesEnum.Town:
                return { [`${pointKey}/townId`]: value.id };
            case regionTypesEnum.Village:
                return { [`${pointKey}/villageId`]: value.id };
        }
    };

    const paramsQuery = {
        ...(startDate && endDate ? { StartedAt: { gt: startDate }, EndedAt: { lt: endDate } } : {}),
        ...(firstPoint ? pointFilter("firstPoint") : {}),
        ...(lastPoint ? pointFilter("lastPoint") : {}),
        ...(legalPersonId?.length ? { legalPersonId: { in: legalPersonId } } : {}),
    };

    const filter = trailerTypeId ? { bids: { any: { trailerTypeId } } } : {};
    const pointQuery = encodeURIComponent(buildQuery({ filter }).substr(1).replace(/'/g, ""))
        .replace("(", "%28")
        .replace(")", "%29");
    const dateQuery = buildQuery({ filter: paramsQuery }).substr(1).replace(/'/g, "");

    return `paramsQuery=${dateQuery}&filterQuery=${pointQuery}`;
};

const deviationVolumeFilter = ({ key, value }: { key: string; value: number }) => {
    if (!value) {
        return {};
    }

    value = Math.abs(value);
    if (key === "eq") {
        return { or: [{ deviationVolume: value }, { deviationVolume: -value }] };
    }
    return key === "ge"
        ? { or: [{ deviationVolume: { ge: value } }, { deviationVolume: { le: -value } }] }
        : { and: [{ deviationVolume: { le: value } }, { deviationVolume: { ge: -value } }] };
};

const reportsOdataQuery = (params: ReportParamsModel) => {
    const {
        id,
        startDate,
        endDate,
        multipleCarTypeId,
        carTypeId,
        transportColumnId,
        onlyWithoutApp,
        marginalityValues,
        visitStatus,
        deviationVolume,
        legalPersonId,
        logistIds,
        trailerTypeIds,
        trailerTypeFamilyTypeIds,
        transportColumnIds,
        region,
        ...rest
    } = params;
    const dateFilterKeys = ["month", "reportPeriod"];
    dateFilterKeys.forEach((key: "month" | "reportPeriod") => {
        if (rest[key]) {
            const date = new Date(rest[key]!);
            if (Number.isNaN(date.getTime())) {
                delete rest[key];
            } else {
                rest[key] = date;
            }
        }
    });
    const dateFilter =
        startDate && endDate ? { StartedAt: { gt: new Date(startDate) }, EndedAt: { lt: new Date(endDate) } } : {};
    const marginalityValuesFilter =
        marginalityValues && marginalityValues !== "all"
            ? { showEstimatedValues: marginalityValues === "estimated", showFactValues: marginalityValues === "fact" }
            : {};
    const filter = {
        ...(onlyWithoutApp ? { null: null } : {}),
        ...(legalPersonId?.length ? { legalPersonIds: { any: { [ITEM_ROOT]: { in: legalPersonId } } } } : {}),
        ...(logistIds?.length ? { logistIds: { any: { [ITEM_ROOT]: { in: logistIds } } } } : {}),
        ...(trailerTypeIds?.length ? { trailerTypeIds: { any: { [ITEM_ROOT]: { in: trailerTypeIds } } } } : {}),
        ...(trailerTypeFamilyTypeIds?.length
            ? { trailerTypeFamilyTypeIds: { any: { [ITEM_ROOT]: { in: trailerTypeFamilyTypeIds } } } }
            : {}),
        ...(transportColumnIds?.length
            ? { transportColumnIds: { any: { [ITEM_ROOT]: { in: transportColumnIds } } } }
            : {}),
        ...dateFilter,
        ...marginalityValuesFilter,
        ...rest,
    };
    const query = buildQuery({ filter }).substr(1);
    let externalFilter: {
        transportColumnId?: { in: number[] };
        carTypeId?: number;
        firstSignIn?: null;
        visitStatus?: { in: string[] };
    } = {};
    if (transportColumnId?.length > 0) {
        externalFilter.transportColumnId = { in: transportColumnId };
    }
    if (multipleCarTypeId || carTypeId) {
        externalFilter.carTypeId = multipleCarTypeId || carTypeId;
    }
    if (onlyWithoutApp) {
        externalFilter.firstSignIn = null;
    }
    if (visitStatus && visitStatus.length > 0) {
        externalFilter.visitStatus = { in: visitStatus };
    }
    if (deviationVolume) {
        externalFilter = { ...externalFilter, ...deviationVolumeFilter(deviationVolume) };
    }
    const regionFilter = region ? { [GEOZONE_FIELDS_BY_REGION_TYPES[region.type]]: region.id } : {};
    const resultFilter = { ...externalFilter, ...regionFilter };
    let externalQuery = Object.keys(resultFilter).length > 0 ? buildQuery({ filter: resultFilter }).substr(1) : null;
    if (externalQuery && id !== REPORT_TYPES.RefuelingsReport) {
        externalQuery = externalQuery.replace(/'/g, "");
    }
    return Object.keys(resultFilter).length > 0
        ? `paramsQuery=${query}&filterQuery=${externalQuery}`
        : `paramsQuery=${query}`;
};

const getQueryByReportName = (params: ReportParamsModel | ReportCounterpartyParamsModel) => {
    switch (params.id) {
        case REPORT_TYPES.CounterpartiesReport:
            return reportsOdataCounterpartiesQuery(params as ReportCounterpartyParamsModel);
        case REPORT_TYPES.AccountsReceivableReport:
        case REPORT_TYPES.AccountsPayableReport:
            return settlementQuery({ ...params, isAccountsPayable: params.id === REPORT_TYPES.AccountsPayableReport });
        default:
            return reportsOdataQuery(params as ReportParamsModel);
    }
};

const getItem = (params: ReportParamsModel | ReportCounterpartyParamsModel) => {
    const { id } = params;
    const query = id === REPORT_TYPES.DriversStatsOfYearReport ? "" : getQueryByReportName(params);
    return request(`${resource}/getDataQuery/${id}/?${query}`, { method: "GET" }).then((res) => {
        if (!res) {
            return res;
        }
        if ([REPORT_TYPES.AccountsReceivableReport, REPORT_TYPES.AccountsPayableReport].includes(id)) {
            res.name = "Взаиморасчёты";
        }
        switch (id) {
            case REPORT_TYPES.CitiesReport:
                if (params.groupByRegion) {
                    res.data = res.data.reduce(
                        (acc: { state: string; stateData: {} }[], item: { state: string; cities: [] }) => {
                            const { cities, ...rest } = item;
                            acc.push(
                                ...cities.map((cityData: {}) => ({ ...cityData, state: item.state, stateData: rest })),
                            );
                            return acc;
                        },
                        [],
                    );
                }
                break;
            case REPORT_TYPES.DailyVehicleMileageReport:
                res.data.forEach((item: { [x: string]: any }) => {
                    res.columns.forEach((column: { [x: string]: any }) => {
                        if (column.index !== null) {
                            item[column.propertyName] = item.additionalData[column.index];
                            res.footer[column.propertyName] = res.footer.additionalData[column.index];
                        }
                    });
                });
                break;
        }
        return res;
    });
};

const generateOverallReport = (params: { parametersQuery: string; filterQuery: string }) => {
    params.parametersQuery = params.parametersQuery.replace("paramsQuery=", "");
    if (params.filterQuery) {
        params.filterQuery = params.filterQuery.replace("filterQuery=", "");
    }
    return request(`${resource}/generateOverallReport`, body(params)).then((res) => (res ? res.key : false));
};

const getGeneratedReport = (key: string) => request(`${resource}/getGeneratedReport?key=${key}`, { method: "GET" });

const generatePlanPointIntersectionsReport = (params: {}) =>
    request(`${resource}/generatePlanPointIntersectionsReport`, body(params)).then((res) => (res ? res.key : false));

const downloadGeneratedReport = (key: string, fileName: string) =>
    request(`${resource}/getGeneratedReport?key=${key}`, { method: "GET" }, { withoutMiddleware: true }).then((res) =>
        downloadFileHandler(res, fileName),
    );

const download = (params: (ReportCounterpartyParamsModel | ReportParamsModel) & { fileName: string }) => {
    const { fileName, ...restParams } = params;
    const rest = restParams as ReportParamsModel | ReportCounterpartyParamsModel;
    const query = getQueryByReportName(rest);

    return request(
        `${resource}/getExcelStreamQuery/${rest.id}/?${query}`,
        { method: "GET" },
        { withoutMiddleware: true },
    ).then((res) => downloadFileHandler(res, fileName));
};

const getPlanItem = (params: {
    id: keyof typeof REPORT_TYPES;
    startDate: Date;
    endDate: Date;
    location: { id: string; type: keyof typeof GEOZONE_FIELDS_BY_REGION_TYPES };
}) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, startDate, endDate, location, ...filters } = params;
    const dateFilter = filters;
    const pointFilter = location ? { [GEOZONE_FIELDS_BY_REGION_TYPES[location.type]]: location.id } : {};
    const pointQuery = encodeURIComponent(buildQuery({ filter: pointFilter }).substr(1).replace(/'/g, ""))
        .replace("(", "%28")
        .replace(")", "%29");
    const dateQuery = buildQuery({ filter: dateFilter }).substr(1).replace(/'/g, "");
    return request(`${resource}/getData/${id}/false/${[dateQuery || " ", pointQuery].join("/")}`, { method: "GET" });
};

export {
    index,
    getMetadata,
    getItem,
    getPlanItem,
    download,
    getGeneratedReport,
    downloadGeneratedReport,
    getQueryByReportName,
    generateOverallReport,
    generatePlanPointIntersectionsReport,
    accountsReceivableReportDemo,
    incomesAndExpensesReportDemo,
};
